import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  CommunityGroupCreateRequest,
  CommunityGroupEditRequest,
  CommunityGroupV2Dto,
  ResidentGroupCreateRequest,
  ResidentGroupEditRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiQueryParams, ApiResponseType } from "@/types/api-types";

const useAddGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: CommunityGroupCreateRequest) => api.postGroupsV2(payload).then((r) => r.data),

    onSuccess: (_, payload) => {
      let title;
      if (payload.type === "helpCategory") {
        title = t("model.help-category.action.create.notification.success");
      } else if (payload.type === "realEstate") {
        title = t("model.real-estate-group.action.create.notification.success");
      } else {
        title = t("model.interest-group.action.create.notification.success");
      }

      showFlashToast({ variant: "success", message: title, "data-testid": "toast-group-is-created" });
    },

    onError: (_, payload) => {
      let title;
      if (payload.type === "helpCategory") {
        title = t("model.help-category.action.create.notification.error");
      } else if (payload.type === "realEstate") {
        title = t("model.real-estate-group.action.create.notification.error");
      } else {
        title = t("model.interest-group.action.create.notification.error");
      }

      showFlashToast({ variant: "alert", message: title });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useUpdateGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: {
      groupId: string;
      data: CommunityGroupEditRequest;
      isRealEstate?: boolean;
      groupType: CommunityGroupV2Dto["type"];
    }) => api.putGroupsV2(payload.groupId, payload.data).then((r) => r.data),

    onSuccess: (_, payload) => {
      let title;
      if (payload.groupType === "helpCategory") {
        title = t("model.help-category.action.edit.notification.success");
      } else if (payload.isRealEstate) {
        title = t("model.real-estate-group.action.edit.notification.success");
      } else {
        title = t("model.interest-group.action.edit.notification.success");
      }

      showFlashToast({ variant: "success", message: title, "data-testid": "toast-group-is-edited" });
    },

    onError: (_, payload) => {
      let title;
      if (payload.groupType === "helpCategory") {
        title = t("model.help-category.action.edit.notification.error");
      } else if (payload.isRealEstate) {
        title = t("model.real-estate-group.action.edit.notification.error");
      } else {
        title = t("model.interest-group.action.edit.notification.error");
      }

      showFlashToast({ variant: "alert", message: title });
    },

    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUP_DETAILS(projectId, payload.groupId),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useDeleteGroup = () => {
  const api = useApi();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupType: CommunityGroupV2Dto["type"] }) => api.deleteGroupsByIdV1(id),
    onSuccess: (_, payload) => {
      showFlashToast({
        variant: "success",
        message:
          payload.groupType === "helpCategory"
            ? t("model.help-category.action.delete.notification.success")
            : t("model.interest-group.action.delete.notification.success"),
        "data-testid": "toast-group-is-deleted",
      });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message:
          payload.groupType === "helpCategory"
            ? t("model.help-category.action.delete.notification.error")
            : t("model.interest-group.action.delete.notification.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useEnableGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupType: CommunityGroupV2Dto["type"] }) => api.postGroupsEnableV1(id),
    onSuccess: (_, payload) => {
      showFlashToast({
        variant: "success",
        message:
          payload.groupType === "helpCategory"
            ? t("model.platform.help-category.action.enable.notification.success")
            : t("model.platform.interest-group.action.enable.notification.success"),
        "data-testid": "toast-group-is-enabled",
      });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message:
          payload.groupType === "helpCategory"
            ? t("model.platform.help-category.action.enable.notification.error")
            : t("model.platform.interest-group.action.enable.notification.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useDisableGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupType: CommunityGroupV2Dto["type"] }) => api.deleteGroupsByIdV1(id),
    onSuccess: (_, payload) => {
      showFlashToast({
        variant: "success",
        message:
          payload.groupType === "helpCategory"
            ? t("model.platform.help-category.action.disable.notification.success")
            : t("model.platform.interest-group.action.disable.notification.success"),
        "data-testid": "toast-group-is-disabled",
      });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message:
          payload.groupType === "helpCategory"
            ? t("model.platform.help-category.action.disable.notification.error")
            : t("model.platform.interest-group.action.disable.notification.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useAddResidentGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: ResidentGroupCreateRequest) => api.postResidentGroupsV2(payload).then((r) => r.data),

    onSuccess: () =>
      showFlashToast({
        variant: "success",
        message: t("page.create-resident-group.create.notification.success"),
      }),

    onError: () =>
      showFlashToast({
        variant: "alert",
        message: t("page.create-resident-group.create.notification.failed"),
      }),

    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useUpdateResidentGroup = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { groupId: string; data: ResidentGroupEditRequest }) =>
      api.putResidentGroupsV2(payload.groupId, payload.data).then((r) => r.data),

    onSuccess: () =>
      showFlashToast({
        variant: "success",
        message: t("page.create-resident-group.edit.notification.success"),
      }),

    onError: () =>
      showFlashToast({
        variant: "alert",
        message: t("page.create-resident-group.edit.notification.failed"),
      }),

    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUP_DETAILS(projectId, payload.groupId),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useFollowGroup = (queryArgs?: ApiQueryParams<"getGroupsV2">) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupType: CommunityGroupV2Dto["type"] }) => api.postGroupsFollowV1(id),

    onMutate: ({ id }) => {
      queryClient.setQueryData<ApiResponseType<"getGroupsDetailsV2"> | undefined>(
        QUERY_KEYS.COMMUNITY_GROUP_DETAILS(projectId, id),
        (data) =>
          data && {
            ...data,
            data: {
              ...data.data,
              isMember: true,
              members: {
                ...data.data.members,
                items: [sessionUser, ...data.data.members.items],
                total: data.data.members.total + 1,
              },
            },
          },
      );

      if (queryArgs) {
        queryClient.setQueryData<ApiResponseType<"getGroupsV2"> | undefined>(
          QUERY_KEYS.COMMUNITY_GROUPS_QUERY(projectId, queryArgs),
          (data) =>
            data && {
              ...data,
              data: {
                ...data.data,
                items: data.data.items.map((item) =>
                  item.id === id
                    ? {
                        ...item,
                        isMember: true,
                        members: {
                          ...item.members,
                          items: [sessionUser, ...item.members.items],
                          total: item.members.total + 1,
                        },
                      }
                    : item,
                ),
              },
            },
        );
      }

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMMUNITY_GROUP_MEMBERS(projectId, id) });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message:
          payload.groupType === "helpCategory"
            ? t("model.help-category.action.follow.notification-error")
            : t("model.interest-group.action.follow.notification-error"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useUnfollowGroup = (queryArgs?: ApiQueryParams<"getGroupsV2">) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id }: { id: string; groupType: CommunityGroupV2Dto["type"] }) => api.postGroupsUnfollowV1(id),
    onMutate: ({ id }) => {
      queryClient.setQueryData<ApiResponseType<"getGroupsDetailsV2"> | undefined>(
        QUERY_KEYS.COMMUNITY_GROUP_DETAILS(projectId, id),
        (data) =>
          data && {
            ...data,
            data: {
              ...data.data,
              isMember: false,
              members: {
                ...data.data.members,
                items: data.data.members.items.filter((x) => x.id !== sessionUser.id),
                total: data.data.members.total - 1,
              },
            },
          },
      );
      if (queryArgs) {
        queryClient.setQueryData<ApiResponseType<"getGroupsV2"> | undefined>(
          QUERY_KEYS.COMMUNITY_GROUPS_QUERY(projectId, queryArgs),
          (data) =>
            data && {
              ...data,
              data: {
                ...data.data,
                items: data.data.items.map((item) =>
                  item.id === id
                    ? {
                        ...item,
                        isMember: false,
                        members: {
                          ...item.members,
                          items: item.members.items.filter((x) => x.id !== sessionUser.id),
                          total: item.members.total - 1,
                        },
                      }
                    : item,
                ),
              },
            },
        );
      }

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMMUNITY_GROUP_MEMBERS(projectId, id) });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message:
          payload.groupType === "helpCategory"
            ? t("model.help-category.action.unfollow.notification-error")
            : t("model.interest-group.action.unfollow.notification-error"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS(projectId),
      });
    },
  });
};

const useSkipGroupPromotion = (groupId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: () => api.postGroupsSkipPromotionV1(groupId),

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMMUNITY_GROUP_DETAILS(projectId, groupId),
      });
    },
  });
};

export const communityGroupMutations = {
  useAddGroup,
  useUpdateGroup,
  useEnableGroup,
  useDisableGroup,
  useDeleteGroup,
  useAddResidentGroup,
  useUpdateResidentGroup,
  useFollowGroup,
  useUnfollowGroup,
  useSkipGroupPromotion,
};
